import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, login, logout, selectAuth } from '../../auth/authSlice';
import { AppDispatch } from '../../../store';

/**
 * Login dedicado do contador: /contador
 * Só aceita role contador; admin master continua em /login.
 */
const ContadorLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector(selectAuth);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user?.userType === 'contador') {
      navigate('/invoice-issuance', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !password) return;

    try {
      const result = await dispatch(login({ email, password })).unwrap();
      const userType = result.user?.userType;
      if (userType !== 'contador') {
        dispatch(logout());
        setLocalError('Este login é só para contador. Use /login para o admin master.');
        return;
      }
      navigate('/invoice-issuance', { replace: true });
    } catch {
      // erro já no slice
    }
  };

  const displayError = localError || error;

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#4338ca' }}>
            <ReceiptLongIcon sx={{ fontSize: 40, mr: 1 }} />
            <Typography component="h1" variant="h4" color="inherit">
              Portal do Contador
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Acesso às informações de nota fiscal para emissão
          </Typography>

          {displayError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {displayError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Senha"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 1,
                bgcolor: '#4338ca',
                '&:hover': { bgcolor: '#3730a3' },
              }}
              disabled={loading || !email || !password}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ContadorLoginPage;
